/**
 * Shared luxury microinteractions for the Burgundy Atelier homepage:
 * gentle scroll reveals + a mobile sticky "Shop Collection" CTA. Progressive
 * enhancement only — every element is fully visible without this script.
 */
(() => {
  "use strict";

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- scroll reveal ---------- */
  const revealSelectors = [
    ".gh-section-head",
    ".gh-card",
    ".gh-muse",
    ".gh-craft",
    ".gh-occasion__card",
    ".gh-testimonial",
    ".gh-ritual__step",
    ".gh-care__item",
    ".gh-journal__feature",
    ".gh-journal__row",
    ".gh-stack__card",
    ".gh-diff__signature-copy",
    ".gh-diff__gallery-item",
    ".gh-diff__philosophy-copy",
    ".gh-diff__philosophy-media",
  ];

  if (!prefersReducedMotion && "IntersectionObserver" in window) {
    const revealTargets = document.querySelectorAll(revealSelectors.join(","));

    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.1 }
    );

    revealTargets.forEach((el, index) => {
      el.classList.add("gh-reveal");
      el.style.transitionDelay = `${(index % 4) * 70}ms`;
      revealObserver.observe(el);
    });
  }

  /* ---------- subtle hero parallax ---------- */
  const heroMedia = document.querySelector(".gh-hero__media");

  if (heroMedia && !prefersReducedMotion) {
    const heroSection = heroMedia.closest(".gh-hero");
    let ticking = false;

    const applyParallax = () => {
      ticking = false;
      const rect = heroSection.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      const progress = Math.min(Math.max(-rect.top / rect.height, 0), 1);
      heroMedia.style.transform = `translateY(${progress * 4}%)`;
    };

    window.addEventListener(
      "scroll",
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(applyParallax);
        }
      },
      { passive: true }
    );

    applyParallax();
  }

  /* ---------- subtle macro-image parallax (Gulmohar Difference) ---------- */
  const diffParallaxEls = document.querySelectorAll(".gh-diff__parallax");

  if (diffParallaxEls.length && !prefersReducedMotion) {
    let diffTicking = false;

    const applyDiffParallax = () => {
      diffTicking = false;
      diffParallaxEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        const center = rect.top + rect.height / 2 - window.innerHeight / 2;
        const progress = Math.min(Math.max(center / window.innerHeight, -1), 1);
        el.style.transform = `translateY(${progress * -14}px)`;
      });
    };

    window.addEventListener(
      "scroll",
      () => {
        if (!diffTicking) {
          diffTicking = true;
          requestAnimationFrame(applyDiffParallax);
        }
      },
      { passive: true }
    );

    applyDiffParallax();
  }

  /* ---------- thickness scale fill (Gulmohar Difference) ---------- */
  const scaleEl = document.querySelector("[data-gh-scale]");

  if (scaleEl && "IntersectionObserver" in window) {
    new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            scaleEl.classList.add("is-active");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    ).observe(scaleEl);
  }

  /* ---------- curated category rail: scroll progress dots ---------- */
  const curatedRail = document.querySelector("[data-gh-rail]");
  const curatedProgress = document.querySelector("[data-gh-rail-progress]");

  if (curatedRail && curatedProgress) {
    const curatedDots = curatedProgress.querySelectorAll(".gh-curated__progress-dot");
    let railTicking = false;

    const updateRailProgress = () => {
      railTicking = false;
      const maxScroll = curatedRail.scrollWidth - curatedRail.clientWidth;
      const fraction = maxScroll > 0 ? curatedRail.scrollLeft / maxScroll : 0;
      const activeIndex = Math.round(fraction * (curatedDots.length - 1));

      curatedDots.forEach((dot, index) => {
        dot.classList.toggle("is-active", index === activeIndex);
      });
    };

    curatedRail.addEventListener(
      "scroll",
      () => {
        if (!railTicking) {
          railTicking = true;
          requestAnimationFrame(updateRailProgress);
        }
      },
      { passive: true }
    );

    updateRailProgress();
  }

  /* ---------- mobile sticky "Shop Collection" CTA ---------- */
  const hero = document.querySelector(".gh-hero");
  const flagship = document.querySelector(".gh-flagship");
  const heroCta = document.querySelector(".gh-hero .gh-btn:not(.gh-btn--line)");

  if (hero && flagship && heroCta && "IntersectionObserver" in window) {
    const bar = document.createElement("div");
    bar.className = "gh-sticky-cta";
    bar.innerHTML = `<a class="gh-btn" href="${heroCta.getAttribute("href")}">Shop Collection</a>`;
    document.body.appendChild(bar);

    let pastHero = false;
    let reachedFlagship = false;

    const updateBar = () => {
      bar.classList.toggle("is-active", pastHero && !reachedFlagship);
    };

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          updateBar();
        });
      },
      { threshold: 0 }
    ).observe(hero);

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          reachedFlagship = entry.isIntersecting;
          updateBar();
        });
      },
      { threshold: 0 }
    ).observe(flagship);
  }
})();
